import { Controller } from "@hotwired/stimulus"

export default class FormValidationController extends Controller {
  static targets = ["submit", "requiredInput", "error"]

  // Set on a form whose required field arrives with a frame rather than with
  // the page. Until it does there is nothing to validate, and a form with no
  // fields in it is not a complete one.
  static values = { awaitInputs: Boolean }

  connect() {
    this.boundValidateForm = this.validateForm.bind(this)
    this.boundHandleBlur = this.handleBlur.bind(this)
    this.boundHandleInput = this.handleInput.bind(this)

    this.requiredInputTargets.forEach((input) => {
      this.bind(input)
    })

    this.validateForm()
  }

  disconnect() {
    this.requiredInputTargets.forEach((input) => {
      this.unbind(input)
    })
  }

  // A required field can arrive after connect(), when a Turbo Stream swaps part
  // of the form — the withdraw screen replaces its amount entry once the bank
  // decides which currency is being typed in. Without these, such a field is
  // never listened to and never validated, and the submit button unlocks with
  // it still empty.
  requiredInputTargetConnected(input) {
    this.bind(input)
    this.validateForm()
  }

  requiredInputTargetDisconnected(input) {
    this.unbind(input)
    this.validateForm()
  }

  bind(input) {
    input.addEventListener("input", this.boundHandleInput)
    input.addEventListener("change", this.boundHandleInput)
    input.addEventListener("blur", this.boundHandleBlur)
  }

  unbind(input) {
    input.removeEventListener("input", this.boundHandleInput)
    input.removeEventListener("change", this.boundHandleInput)
    input.removeEventListener("blur", this.boundHandleBlur)
  }

  handleInput(event) {
    const input = event.target


    this.validateForm()
  }

  handleBlur(event) {
    const input = event.target

    const trimmed = input.value.trim()
    if (trimmed !== input.value) {
      input.value = trimmed
    }

    input.dataset.touched = "true"
    this.validateForm()
  }

  validateForm() {
    if (this.awaitInputsValue && this.requiredInputTargets.length === 0) {
      this.updateSubmitButton(false)
      return
    }

    let isFormValid = true

    this.requiredInputTargets.forEach((input) => {
      const fieldValid = this.validateInputField(input)
      if (!fieldValid) {
        isFormValid = false
      }
    })

    this.updateSubmitButton(isFormValid)
  }

  validateInputField(input) {
    const value = input.value.replaceAll(/\s+/g, '')
    const validationType = input.dataset.validationType
    const regexPattern = input.dataset.validationRegex
    const errorMessage = input.dataset.validationMessage
    const isTouched = input.dataset.touched === "true"

    if (!value) {
      this.updateErrorDisplay(input, errorMessage, isTouched, false)
      return false
    }

    if (validationType === "positive-number" && !this.isValidPositiveNumber(value)) {
      this.updateErrorDisplay(input, errorMessage, isTouched, false)
      return false
    }

    if (regexPattern && !this.isValidRegex(value, regexPattern)) {
      this.updateErrorDisplay(input, errorMessage, isTouched, false)
      return false
    }

    this.updateErrorDisplay(input, errorMessage, isTouched, true)
    return true
  }

  isValidPositiveNumber(value) {
    const numericValue = Number.parseFloat(value)
    return !Number.isNaN(numericValue) && numericValue > 0
  }

  isValidRegex(value, regexPattern) {
    try {
      const re = new RegExp(regexPattern)
      return re.test(value)
    } catch (e) {
      console.warn("Invalid regex pattern:", regexPattern, e)
      return false
    }
  }

  updateErrorDisplay(input, errorMessage, isTouched, fieldValid) {
    if (!this.hasErrorTarget) return

    const errorEl = this.errorTargets.find(
      (el) => el.dataset.errorFor === input.name,
    )

    if (errorEl) {
      if (isTouched && !fieldValid) {
        errorEl.textContent = errorMessage || "Invalid value"
        errorEl.classList.remove("hidden")
      } else {
        errorEl.textContent = ""
        errorEl.classList.add("hidden")
      }
    }
  }

  updateSubmitButton(isFormValid) {
    if (!this.hasSubmitTarget) return

    this.submitTarget.disabled = !isFormValid

    if (isFormValid) {
      this.submitTarget.classList.remove(
        "opacity-50",
        "cursor-not-allowed",
        "bg-gray-600",
      )
      this.submitTarget.classList.add("hover:bg-gray-900", "bg-black")
    } else {
      this.submitTarget.classList.add(
        "opacity-50",
        "cursor-not-allowed",
        "bg-gray-600",
      )
      this.submitTarget.classList.remove("hover:bg-gray-900", "bg-black")
    }
  }
}
